import { resolveMediaUrl } from '../../utils/helpers';
import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, ChevronLeft, ChevronRight, UserCheck, Users, Filter } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { academyAPI } from '../../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcAge(dob) {
  if (!dob) return null;
  const diff = Date.now() - new Date(dob).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
}

function getAgeGroup(dob) {
  const age = calcAge(dob);
  if (age === null) return null;
  if (age < 6)  return 'U6';
  if (age < 8)  return 'U8';
  if (age < 10) return 'U10';
  if (age < 12) return 'U12';
  if (age < 14) return 'U14';
  if (age < 16) return 'U16';
  if (age < 18) return 'U18';
  return 'Adult';
}

const AGE_GROUPS = ['U6', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adult'];

function Avatar({ member, size = 10 }) {
  const initials = `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase();
  if (member.profileImage) {
    return (
      <img
        src={resolveMediaUrl(member.profileImage)}
        alt={`${member.firstName} ${member.lastName}`}
        className={`w-${size} h-${size} rounded-full object-cover flex-shrink-0`}
        onError={e => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3b82f6&color=fff&size=64`; }}
      />
    );
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-xs flex-shrink-0`}>
      {initials || '?'}
    </div>
  );
}

function MemberRow({ member, selected, onToggle, showCheck = true }) {
  const { theme, isDark } = useTheme();
  const ageGroup = getAgeGroup(member.dob);

  // Selection had to stop using Tailwind's `dark:` variant. darkMode is unset
  // in tailwind.config.js, so `dark:` tracks the operating system rather than
  // the theme the user chose — pick a dark theme on a light Mac and the row
  // took the near-white `bg-blue-50` while the name stayed theme.text (white),
  // which is white text on a white row. isDark follows the actual theme.
  const rowTone = selected
    ? (isDark ? 'bg-blue-400/25 border border-blue-300/60' : 'bg-blue-50 border border-blue-200')
    : `${theme.cardHover} border border-transparent`;

  // Unselected checkboxes used theme.border, which every dark theme sets to
  // its own card colour — so the box was invisible and rows looked
  // unselectable until clicked.
  const boxTone = selected
    ? 'bg-blue-500 border-blue-500'
    : (isDark ? 'border-white/50' : theme.border);

  return (
    <div
      onClick={onToggle}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${rowTone}`}
    >
      {showCheck && (
        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${boxTone}`}>
          {selected && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 fill-white"><path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>}
        </div>
      )}
      <Avatar member={member} size={8} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${theme.text} truncate`}>
          {member.firstName} {member.lastName}
        </p>
        {(ageGroup || member.gender) && (
          <p className={`text-xs ${theme.textMuted} truncate`}>
            {[member.gender, ageGroup].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────────

const ProgramEnrollmentModal = ({ isOpen, onClose, academyId, program }) => {
  const { theme } = useTheme();

  const [enrolled, setEnrolled]         = useState([]);   // enrolled members
  const [available, setAvailable]       = useState([]);   // all academy members
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  // Selection state
  const [selectedEnrolled, setSelectedEnrolled]   = useState(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState(new Set());

  // Filters
  const [enrolledSearch, setEnrolledSearch]     = useState('');
  const [availableSearch, setAvailableSearch]   = useState('');
  const [genderFilter, setGenderFilter]         = useState('');
  const [ageGroupFilter, setAgeGroupFilter]     = useState('');

  useEffect(() => {
    if (!isOpen || !academyId || !program?.id) return;
    setLoading(true);
    setSelectedEnrolled(new Set());
    setSelectedAvailable(new Set());
    setEnrolledSearch('');
    setAvailableSearch('');
    setGenderFilter('');
    setAgeGroupFilter('');

    Promise.all([
      academyAPI.enrollments.list(academyId, program.id),
      academyAPI.members.list(academyId, { limit: 500 }),
    ]).then(([enrolledData, membersData]) => {
      const enrolledList = Array.isArray(enrolledData) ? enrolledData : [];
      const allMembers   = membersData?.members || [];
      const enrolledMemberIds = new Set(enrolledList.map(e => e.memberId));
      setEnrolled(enrolledList);
      setAvailable(allMembers.filter(m => !enrolledMemberIds.has(m.memberId)));
    }).catch(() => toast.error('Failed to load enrollment data'))
      .finally(() => setLoading(false));
  }, [isOpen, academyId, program?.id]);

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filteredEnrolled = useMemo(() => {
    const q = enrolledSearch.trim().toLowerCase();
    return enrolled.filter(m => {
      if (q && !`${m.firstName} ${m.lastName}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [enrolled, enrolledSearch]);

  const filteredAvailable = useMemo(() => {
    const q = availableSearch.trim().toLowerCase();
    return available.filter(m => {
      if (q && !`${m.firstName} ${m.lastName}`.toLowerCase().includes(q)) return false;
      if (genderFilter && (m.gender || '').toLowerCase() !== genderFilter.toLowerCase()) return false;
      if (ageGroupFilter && getAgeGroup(m.dob) !== ageGroupFilter) return false;
      return true;
    });
  }, [available, availableSearch, genderFilter, ageGroupFilter]);

  // ── Transfer actions ──────────────────────────────────────────────────────

  const moveToEnrolled = async () => {
    if (selectedAvailable.size === 0) return;

    // Enforce seat cap
    if (program?.maxParticipants != null) {
      const seatsLeft = program.maxParticipants - enrolled.length;
      if (selectedAvailable.size > seatsLeft) {
        toast.error(
          seatsLeft <= 0
            ? 'This program is at full capacity'
            : `Only ${seatsLeft} seat${seatsLeft !== 1 ? 's' : ''} left — select fewer students`
        );
        return;
      }
    }

    const memberIds = [...selectedAvailable];
    setSaving(true);
    try {
      await academyAPI.enrollments.enroll(academyId, program.id, memberIds);
      const toMove = available.filter(m => selectedAvailable.has(m.memberId));
      setEnrolled(prev => [...prev, ...toMove.map(m => ({ ...m, enrolledAt: new Date().toISOString(), status: 'active' }))]);
      setAvailable(prev => prev.filter(m => !selectedAvailable.has(m.memberId)));
      setSelectedAvailable(new Set());
      toast.success(`${memberIds.length} student${memberIds.length !== 1 ? 's' : ''} enrolled`);
    } catch (err) {
      toast.error(err?.message || 'Failed to enroll students');
    } finally {
      setSaving(false);
    }
  };

  const moveToAvailable = async () => {
    if (selectedEnrolled.size === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        [...selectedEnrolled].map(memberId => academyAPI.enrollments.unenroll(academyId, program.id, memberId))
      );
      const toMove = enrolled.filter(m => selectedEnrolled.has(m.memberId));
      setAvailable(prev => [...prev, ...toMove]);
      setEnrolled(prev => prev.filter(m => !selectedEnrolled.has(m.memberId)));
      setSelectedEnrolled(new Set());
      toast.success(`${toMove.length} student${toMove.length !== 1 ? 's' : ''} removed`);
    } catch (err) {
      toast.error(err?.message || 'Failed to remove students');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnrolled  = (memberId) => setSelectedEnrolled(s => { const n = new Set(s); n.has(memberId) ? n.delete(memberId) : n.add(memberId); return n; });
  const toggleAvailable = (memberId) => setSelectedAvailable(s => { const n = new Set(s); n.has(memberId) ? n.delete(memberId) : n.add(memberId); return n; });

  const selectAllAvailable = () => setSelectedAvailable(new Set(filteredAvailable.map(m => m.memberId)));
  const clearAllAvailable  = () => setSelectedAvailable(new Set());

  const genders = useMemo(() => {
    const set = new Set(available.map(m => m.gender).filter(Boolean));
    return [...set].sort();
  }, [available]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`${theme.card} border rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col`} style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${theme.border} flex-shrink-0`}>
          <div>
            <h2 className={`text-xl font-bold ${theme.text}`}>Assign Students</h2>
            <div className="flex items-center gap-3 mt-0.5">
              <p className={`text-sm ${theme.textMuted}`}>{program?.name}</p>
              {!loading && (
                program?.maxParticipants != null
                  ? (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      enrolled.length >= program.maxParticipants
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {Math.max(0, program.maxParticipants - enrolled.length)} of {program.maxParticipants} seats left
                    </span>
                  ) : (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      Unlimited capacity
                    </span>
                  )
              )}
              {program?.endDate && new Date(program.endDate) < new Date() && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  Program ended
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${theme.cardHover}`}>
            <X size={20} className={theme.textSecondary} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">

            {/* ── Left: Enrolled ── */}
            <div className="flex-1 flex flex-col border-r" style={{ borderColor: 'var(--border-color)' }}>
              <div className={`px-4 py-3 border-b ${theme.border} flex-shrink-0`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <UserCheck size={16} className="text-green-500" />
                    <span className={`text-sm font-semibold ${theme.text}`}>
                      Enrolled
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">{enrolled.length}</span>
                    </span>
                  </div>
                  {selectedEnrolled.size > 0 && (
                    <span className={`text-xs ${theme.textMuted}`}>{selectedEnrolled.size} selected</span>
                  )}
                </div>
                <div className="relative">
                  <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
                  <input
                    type="text"
                    value={enrolledSearch}
                    onChange={e => setEnrolledSearch(e.target.value)}
                    placeholder="Search enrolled..."
                    className={`w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg ${theme.input}`}
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {filteredEnrolled.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                    <UserCheck size={36} className={`mb-2 ${theme.textMuted} opacity-30`} />
                    <p className={`text-sm ${theme.textMuted}`}>
                      {enrolled.length === 0 ? 'No students enrolled yet' : 'No matches'}
                    </p>
                  </div>
                ) : filteredEnrolled.map(m => (
                  <MemberRow
                    key={m.memberId}
                    member={m}
                    selected={selectedEnrolled.has(m.memberId)}
                    onToggle={() => toggleEnrolled(m.memberId)}
                  />
                ))}
              </div>
            </div>

            {/* ── Center: Transfer buttons ── */}
            <div className="flex flex-col items-center justify-center gap-3 px-3 flex-shrink-0">
              <button
                onClick={moveToEnrolled}
                disabled={selectedAvailable.size === 0 || saving}
                title={`Enroll ${selectedAvailable.size} selected`}
                className={`p-2.5 rounded-xl border-2 transition-all ${
                  selectedAvailable.size > 0 && !saving
                    ? 'bg-blue-500 border-blue-500 text-white hover:bg-blue-600 shadow-md'
                    : `${theme.border} ${theme.textMuted} opacity-40 cursor-not-allowed`
                }`}
              >
                <ChevronLeft size={20} />
              </button>
              {(selectedAvailable.size > 0 || selectedEnrolled.size > 0) && (
                <div className={`text-xs ${theme.textMuted} text-center`} style={{ maxWidth: 48 }}>
                  {selectedAvailable.size > 0 && <div className="text-blue-500 font-bold">{selectedAvailable.size}</div>}
                  {selectedEnrolled.size > 0  && <div className="text-red-400 font-bold">{selectedEnrolled.size}</div>}
                </div>
              )}
              <button
                onClick={moveToAvailable}
                disabled={selectedEnrolled.size === 0 || saving}
                title={`Remove ${selectedEnrolled.size} selected`}
                className={`p-2.5 rounded-xl border-2 transition-all ${
                  selectedEnrolled.size > 0 && !saving
                    ? 'bg-pink-500 border-pink-500 text-white hover:bg-pink-600 shadow-md'
                    : `${theme.border} ${theme.textMuted} opacity-40 cursor-not-allowed`
                }`}
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* ── Right: Available ── */}
            <div className="flex-1 flex flex-col">
              <div className={`px-4 py-3 border-b ${theme.border} flex-shrink-0`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-blue-500" />
                    <span className={`text-sm font-semibold ${theme.text}`}>
                      Available
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{filteredAvailable.length}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedAvailable.size > 0 && (
                      <span className={`text-xs ${theme.textMuted}`}>{selectedAvailable.size} selected</span>
                    )}
                    {filteredAvailable.length > 0 && (
                      <button
                        onClick={selectedAvailable.size === filteredAvailable.length ? clearAllAvailable : selectAllAvailable}
                        className="text-xs text-blue-500 hover:underline"
                      >
                        {selectedAvailable.size === filteredAvailable.length ? 'Deselect all' : 'Select all'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Search */}
                <div className="relative mb-2">
                  <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
                  <input
                    type="text"
                    value={availableSearch}
                    onChange={e => setAvailableSearch(e.target.value)}
                    placeholder="Search students..."
                    className={`w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg ${theme.input}`}
                  />
                </div>

                {/* Filters */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Filter size={12} className={`absolute left-2 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
                    <select
                      value={genderFilter}
                      onChange={e => setGenderFilter(e.target.value)}
                      className={`w-full pl-6 pr-2 py-1.5 text-xs border rounded-lg ${theme.input} appearance-none`}
                    >
                      <option value="">All genders</option>
                      {genders.map(g => (
                        <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="relative flex-1">
                    <select
                      value={ageGroupFilter}
                      onChange={e => setAgeGroupFilter(e.target.value)}
                      className={`w-full px-2 py-1.5 text-xs border rounded-lg ${theme.input}`}
                    >
                      <option value="">All ages</option>
                      {AGE_GROUPS.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {filteredAvailable.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                    <Users size={36} className={`mb-2 ${theme.textMuted} opacity-30`} />
                    <p className={`text-sm ${theme.textMuted}`}>
                      {available.length === 0 ? 'All students are enrolled' : 'No students match filters'}
                    </p>
                    {(genderFilter || ageGroupFilter || availableSearch) && (
                      <button
                        onClick={() => { setGenderFilter(''); setAgeGroupFilter(''); setAvailableSearch(''); }}
                        className="mt-2 text-xs text-blue-500 hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                ) : filteredAvailable.map(m => (
                  <MemberRow
                    key={m.memberId}
                    member={m}
                    selected={selectedAvailable.has(m.memberId)}
                    onToggle={() => toggleAvailable(m.memberId)}
                  />
                ))}
              </div>
            </div>

          </div>
        )}

        {/* Footer */}
        <div className={`px-6 py-3 border-t ${theme.border} flex items-center justify-between flex-shrink-0`}>
          <p className={`text-xs ${theme.textMuted}`}>
            Select students on either side, then use the arrows to enroll or remove.
          </p>
          <button onClick={onClose} className={`px-5 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm`}>
            Done
          </button>
        </div>

      </div>
    </div>
  );
};

export default ProgramEnrollmentModal;
